/**
 * SessionLinkCard — Klickbare Session-Empfehlung im Chat
 * Wird von ChatMarkdown gerendert wenn der Coach [SESSION:id|Titel] empfiehlt.
 * Navigiert den Nutzer direkt zur Session (Voice oder Chat).
 */
import { useLocation } from "wouter";
import { Play, MessageCircle, Sparkles } from "lucide-react";

// Session metadata for rendering the correct icon and mode
const SESSION_META: Record<
  string,
  {
    mode: "voice" | "chat" | "audio";
    icon: "play" | "chat" | "sparkles";
    contextPrompt: string;
  }
> = {
  // Live Voice Sessions
  "live-dinacharya": {
    mode: "voice",
    icon: "play",
    contextPrompt: "Ich möchte meine Tagesroutine (Dinacharya) reflektieren.",
  },
  "live-coaching": {
    mode: "voice",
    icon: "play",
    contextPrompt: "Ich möchte ein persönliches Ayurveda-Coaching.",
  },
  "live-dosha-stress": {
    mode: "voice",
    icon: "play",
    contextPrompt: "Ich fühle mich gestresst und aus der Balance.",
  },
  "live-vata": {
    mode: "voice",
    icon: "play",
    contextPrompt: "Ich spüre innere Unruhe und Ängste.",
  },
  "live-sankalpa": {
    mode: "voice",
    icon: "play",
    contextPrompt: "Ich möchte mein Sankalpa finden.",
  },
  "live-sattva": {
    mode: "voice",
    icon: "play",
    contextPrompt: "Ich habe einen Konflikt, der mich belastet.",
  },
  // Chat Exercises
  "chat-dosha-check": {
    mode: "chat",
    icon: "chat",
    contextPrompt: "Starte einen schnellen Phänotyp-Tages-Check mit mir.",
  },
  "chat-dankbarkeit": {
    mode: "chat",
    icon: "chat",
    contextPrompt: "Führe mich durch ein Dankbarkeits-Ritual.",
  },
  "chat-dharma": {
    mode: "chat",
    icon: "chat",
    contextPrompt: "Führe mich durch eine Dharma-Kompass-Übung.",
  },
  "chat-kosha": {
    mode: "chat",
    icon: "chat",
    contextPrompt: "Führe mich durch eine Kosha-Übung.",
  },
  "chat-prakriti": {
    mode: "chat",
    icon: "chat",
    contextPrompt: "Hilf mir, die Stärken meiner Prakriti zu entdecken.",
  },
  "chat-ernaehrung": {
    mode: "chat",
    icon: "chat",
    contextPrompt: "Erstelle mit mir einen sattvischen Ernährungsplan.",
  },
  "chat-ritucharya": {
    mode: "chat",
    icon: "chat",
    contextPrompt: "Führe mich durch eine Ritucharya-Planung.",
  },
  // Audio Sessions (guided)
  "morgen-meditation": { mode: "audio", icon: "sparkles", contextPrompt: "" },
  "atem-4-7-8": { mode: "audio", icon: "sparkles", contextPrompt: "" },
  "body-scan": { mode: "audio", icon: "sparkles", contextPrompt: "" },
  "abend-reflexion": { mode: "audio", icon: "sparkles", contextPrompt: "" },
  muskelentspannung: { mode: "audio", icon: "sparkles", contextPrompt: "" },
  "nadi-shodhana": { mode: "audio", icon: "sparkles", contextPrompt: "" },
  bhramari: { mode: "audio", icon: "sparkles", contextPrompt: "" },
  kapalabhati: { mode: "audio", icon: "sparkles", contextPrompt: "" },
  "dosha-balance": { mode: "audio", icon: "sparkles", contextPrompt: "" },
  "bodyscan-ayurveda": { mode: "audio", icon: "sparkles", contextPrompt: "" },
  dankbarkeit: { mode: "audio", icon: "sparkles", contextPrompt: "" },
  visualisierung: { mode: "audio", icon: "sparkles", contextPrompt: "" },
  "yoga-nidra": { mode: "audio", icon: "sparkles", contextPrompt: "" },
  "chakra-meditation": { mode: "audio", icon: "sparkles", contextPrompt: "" },
  "abhyanga-anleitung": { mode: "audio", icon: "sparkles", contextPrompt: "" },
  "marma-massage": { mode: "audio", icon: "sparkles", contextPrompt: "" },
};

interface SessionLinkCardProps {
  sessionId: string;
  title: string;
}

export default function SessionLinkCard({
  sessionId,
  title,
}: SessionLinkCardProps) {
  const [, setLocation] = useLocation();
  const meta = SESSION_META[sessionId];

  const handleClick = () => {
    if (!meta) {
      // Unknown session — navigate to sessions library
      setLocation("/sessions");
      return;
    }

    if (meta.mode === "audio") {
      // Audio sessions → navigate to sessions page with scroll to that session
      setLocation(`/sessions?highlight=${sessionId}`);
      return;
    }

    // Voice or Chat sessions → navigate to AYU Coach with params
    const params = new URLSearchParams();
    params.set("mode", meta.mode);
    params.set("context", meta.contextPrompt);
    params.set("title", title);
    params.set("sessionId", sessionId);
    setLocation(`/ayu-coach?${params.toString()}`);
  };

  const IconComponent =
    meta?.icon === "play"
      ? Play
      : meta?.icon === "chat"
        ? MessageCircle
        : Sparkles;
  const modeLabel =
    meta?.mode === "voice"
      ? "Live-Gespräch"
      : meta?.mode === "chat"
        ? "Chat-Übung"
        : "Audio-Session";
  const modeColor =
    meta?.mode === "voice"
      ? "bg-[#b8963d]/10 text-[#b8963d] border-[#b8963d]/20"
      : meta?.mode === "chat"
        ? "bg-[#6b7f4e]/10 text-[#6b7f4e] border-[#6b7f4e]/20"
        : "bg-[#7c6fa0]/10 text-[#7c6fa0] border-[#7c6fa0]/20";

  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-2 px-3 py-2 my-1 rounded-xl border transition-all duration-200 hover:shadow-md hover:scale-[1.02] active:scale-[0.98] ${modeColor}`}
    >
      <IconComponent className="w-4 h-4 shrink-0" />
      <span className="text-sm font-medium">{title}</span>
      <span className="text-[10px] opacity-60 ml-1">{modeLabel}</span>
    </button>
  );
}

/**
 * Parse text for [SESSION:id|title] patterns and return segments.
 * Used by ChatMarkdown to render session links inline.
 */
export interface SessionLinkSegment {
  type: "text" | "session";
  content: string;
  sessionId?: string;
  title?: string;
}

export function parseSessionLinks(text: string): SessionLinkSegment[] {
  const regex = /\[SESSION:([a-z0-9-]+)\|([^\]]+)\]/g;
  const segments: SessionLinkSegment[] = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      segments.push({
        type: "text",
        content: text.slice(lastIndex, match.index),
      });
    }
    // Add the session link
    segments.push({
      type: "session",
      content: match[0],
      sessionId: match[1],
      title: match[2],
    });
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    segments.push({ type: "text", content: text.slice(lastIndex) });
  }

  return segments;
}
